import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import document from 'global/document';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import queryString from 'query-string';
import { withTranslation } from 'react-i18next';
import { message } from 'antd';
import * as actions from '~actions/pms-actions/properties/property-list';
import { communicationStatus } from '~constants';
import NoProperties from '~components/no-properties';
import PropertyStatusFilter from '~components/property-status-filter';
import { mergeSearchParams, extractSearchParams } from '~helpers/history';
import { setItem } from '~helpers/storage';
import generatePath from '~settings/routing';
import PropertyLists from './property-lists';
import { trackViewPropertyListPage, trackClickPropertyListPage } from './track';

const mapStateToProps = state => ({
  list: state.pmsReducer.dashboard.propertyList.get('list').toJS(),
  communication: state.pmsReducer.dashboard.propertyList.get('communication').toJS(),
  role: state.pmsReducer.auth.get('auth'),
});

const mapDispatchToProps = dispatch => ({
  initialize: () => {
    dispatch(actions.initialize());
  },

  getProperties: filters => {
    dispatch(actions.getPropertyList(filters));
  },

  setProperty: (index, property) => {
    dispatch(actions.setProperty(index, property));
  },
});

/**
 * TODO: handle communication error
 * URL search params
 * {
 *   pageNumber: 1,
 *   pageSize: 10,
 *   sortBy: null,
 *   sortDirection: null,
 *   countrySlug: null,
 *   citySlug: null,
 *   landlordSlug: null,
 *   statuses: null
 * }
 */
@connect(mapStateToProps, mapDispatchToProps)
@withRouter
@withTranslation()
export default class PropertyList extends React.Component {
  constructor(props) {
    super(props);

    this.defaultSearchParams = {
      pageNumber: 1,
      pageSize: 10,
    };

    this.state = {
      filters: this.formatFilters(queryString.parse(this.props.location.search)),
      loading: false,
      isLarge: false,
    };

    this.propertyTemplate = {
      property: {
        statuses: '',
        slug: '',
        id: '',
        image: '',
        landlordName: '',
        name: '',
        address: '',
        cityName: '',
        zipCode: '',
        updatedAt: '',
      },
    };

    // TO show loading page
    this.loadingFakeData = [
      this.propertyTemplate,
      this.propertyTemplate,
      this.propertyTemplate,
      this.propertyTemplate,
      this.propertyTemplate,
      this.propertyTemplate,
      this.propertyTemplate,
      this.propertyTemplate,
      this.propertyTemplate,
      this.propertyTemplate,
    ];
  }

  componentDidMount() {
    this.props.initialize();

    if (this.props.location.search) {
      setItem('cms_properties_list_filters', this.state.filters);
    }

    this.props.getProperties(this.handleFilters(this.state.filters || {}));
    window.addEventListener('resize', this.largeStyle);
    document.addEventListener('toggleCollapseSidebarTriggers', this.largeStyle);
    this.largeStyle();

    trackViewPropertyListPage();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.largeStyle);
    document.removeEventListener('toggleCollapseSidebarTriggers', this.largeStyle);
  }

  largeStyle = () => {
    const propertyList = document.querySelector('.property-list');
    const propertyListWidth = propertyList ? propertyList.clientWidth : 0;

    if (propertyListWidth + 32 >= 1400 && !this.state.isRequired) {
      this.setState({
        isLarge: true,
      });
    }

    if (propertyListWidth + 32 < 1400) {
      this.setState({
        isLarge: false,
      });
    }
  };

  componentWillReceiveProps(nextProps) {
    if (
      nextProps.communication.status !== communicationStatus.IDLE &&
      nextProps.communication.status !== communicationStatus.FETCHING &&
      nextProps.communication.status !== this.props.communication.status
    ) {
      message.error(this.props.t('cms:cms.message.error'));
    }

    if (
      this.props.communication.status === communicationStatus.IDLE &&
      nextProps.communication.status === communicationStatus.FETCHING
    ) {
      this.state.loading = true;
    }

    if (
      this.props.communication.status === communicationStatus.FETCHING &&
      nextProps.communication.status === communicationStatus.IDLE
    ) {
      this.state.loading = false;
      this.props.handlePendingApprove(!this.isShowNoProperties(nextProps));
    }

    if (nextProps.location.search !== this.props.location.search) {
      this.resetFilters(nextProps.location.search);
    }
  }

  handleFilters = filters => {
    const { type, slug } = extractSearchParams(filters.search);
    const formatFilters = this.formatFilters(filters);
    formatFilters.hasHerculesProperties = this.props.hasHerculesProperties;
    setItem('cms_properties_list_filters', formatFilters);
    if (type && slug) {
      formatFilters[type] = slug;
    }
    return formatFilters;
  };

  formatFilters = filters => ({
    countrySlug: filters.countrySlug || null,
    tag: filters.tag || null,
    pageNumber: Number(filters.pageNumber) || this.defaultSearchParams.pageNumber,
    pageSize: Number(filters.pageSize) || this.defaultSearchParams.pageSize,
    statuses: filters.statuses || null,
    citySlug: filters.citySlug || null,
    landlordSlug: filters.landlordSlug || null,
    search: filters.search || null,
    bookingJourneys: filters.bookingJourneys || null,
    sort: filters.sort || null,
  });

  pushState = data => {
    this.props.history.push(mergeSearchParams(data, this.defaultSearchParams));
  };

  handlePageChange = (page, pageSize) => {
    trackClickPropertyListPage('Pagination');

    this.state.filters.pageNumber = page;

    if (pageSize !== this.state.filters.pageSize) {
      this.state.filters.pageSize = pageSize;
      this.state.filters.pageNumber = this.defaultSearchParams.pageNumber;
    }

    this.props.getProperties(this.handleFilters(this.state.filters));
    window.scroll(0, 0);

    this.pushState(this.state.filters);
  };

  resetFilters = search => {
    const filters = queryString.parse(search);
    this.state.filters = this.formatFilters(Object.assign({}, this.defaultSearchParams, filters));
    this.props.getProperties(this.handleFilters(this.state.filters));
    window.scroll(0, 0);

    this.setState(this.state);
  };

  handleFilterChange = filters => {
    const newFilters = Object.assign({}, this.state.filters, filters, { pageNumber: 1 });
    this.pushState(newFilters);
  };

  isShowNoProperties = props => {
    const { tag, citySlug, landlordSlug, countrySlug, statuses, search, bookingJourneys } =
      this.state.filters;
    if (
      !countrySlug &&
      !statuses &&
      !tag &&
      !citySlug &&
      !landlordSlug &&
      !search &&
      !bookingJourneys &&
      props.list.total === 0 &&
      props.communication.status === communicationStatus.IDLE
    ) {
      return true;
    }

    return false;
  };

  onAddNewBtn = () => {
    const url = generatePath('property.create');

    trackClickPropertyListPage('Create Property');

    this.props.history.push(url);
  };

  render() {
    const { filters } = this.state;

    return (
      <div className="property-list">
        <Choose>
          <When condition={this.isShowNoProperties(this.props)}>
            <NoProperties t={this.props.t} addBtn={this.onAddNewBtn} />
          </When>
          <Otherwise>
            <div className="property-list__header">
              <div className="property-list__header-container">
                <PropertyStatusFilter
                  t={this.props.t}
                  onChange={this.handleFilterChange}
                  total={this.props.list.total}
                  filters={this.state.filters}
                  search={this.props.location.search}
                  addBtn={this.onAddNewBtn}
                  hasHerculesProperties={this.props.hasHerculesProperties}
                  sortBy={filters.sort ? filters.sort : 'updated_at'}
                  isSelected={filters.sort}
                />
              </div>
            </div>
            <div
              className={classNames('property-list__list', {
                'property-list__list-nomal': this.state.isLarge,
              })}
              style={{ width: '100%', overflow: 'scroll' }}
            >
              {/* <List
                size="large"
                dataSource={this.state.loading ? this.loadingFakeData : this.props.list.payload}
                pagination={{
                  showSizeChanger: true,
                  onChange: this.handlePageChange,
                  onShowSizeChange: this.handlePageChange,
                  defaultCurrent: this.defaultSearchParams.pageNumber,
                  total: this.props.list.total,
                  current: this.state.filters.pageNumber,
                  pageSize: this.state.filters.pageSize,
                }}
                renderItem={(item, index) => (
                  <List.Item key={item.title}>
                    <PropertyCard
                      property={item}
                      t={this.props.t}
                      loading={this.state.loading}
                      setProperty={property => {
                        this.props.setProperty(index, property);
                      }}
                    />
                  </List.Item>
                )}
              /> */}

              <PropertyLists
                loading={this.state.loading}
                onChange={this.handleFilterChange}
                dataSource={this.state.loading ? this.loadingFakeData : this.props.list.payload}
                pagination={{
                  showQuickJumper: true,
                  showSizeChanger: true,
                  onChange: this.handlePageChange,
                  onShowSizeChange: this.handlePageChange,
                  defaultCurrent: this.defaultSearchParams.pageNumber,
                  total: this.props.list.total,
                  current: this.state.filters.pageNumber,
                  pageSize: this.state.filters.pageSize,
                }}
              />
            </div>
          </Otherwise>
        </Choose>
      </div>
    );
  }
}

PropertyList.propTypes = {
  list: PropTypes.shape({
    payload: PropTypes.array.isRequired,
    total: PropTypes.number.isRequired,
  }),
  communication: PropTypes.shape({
    status: PropTypes.string.isRequired,
  }),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
  initialize: PropTypes.func.isRequired,
  getProperties: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
  }),
  setProperty: PropTypes.func,
  handlePendingApprove: PropTypes.func,
  role: PropTypes.object,
  hasHerculesProperties: PropTypes.bool,
};

PropertyList.defaultProps = {
  list: {
    payload: [],
    total: 0,
  },
  communication: {
    status: '',
  },
  history: {
    push: () => {},
  },
  initialize: () => {},
  getProperties: () => {},
  handlePendingApprove: () => {},
  setProperty: () => {},
  t: () => {},
  location: {
    search: '',
  },
  role: {},
  hasHerculesProperties: false,
};
